interface OrderIssueItem {
    Name?: string;
    Quantity?: number;
    Reason?: string;
    Size?: {
        Name?: string;
        ColloquialName?: string;
    };
}

interface OrderIssues {
    InvalidMenuItems?: OrderIssueItem[];
    invalidMenuItems?: OrderIssueItem[];
}

const FAILURE_FLAGS = [
    'pos-api-failure',
    'invalid-menu-item',
    'invalid-item-found',
    'menu-item-not-found'
];

const ORDER_ATTEMPT_FLAGS = [
    'checking out',
    'checkout-information-complete',
    'has-ordered',
    'order-placed',
    'voicify-order-attempted'
];

const parseOrderIssues = (value: any): OrderIssues | null => {
    if (!value) {
        return null;
    }

    if (typeof value === 'object') {
        return value as OrderIssues;
    }

    if (typeof value !== 'string') {
        return null;
    }

    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' ? parsed as OrderIssues : null;
    } catch {
        return null;
    }
};

const parseStringArray = (value: any): string[] => {
    if (Array.isArray(value)) {
        return value.filter(item => typeof item === 'string' && item.trim().length > 0);
    }

    if (typeof value !== 'string') {
        return [];
    }

    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed)
            ? parsed.filter(item => typeof item === 'string' && item.trim().length > 0)
            : [];
    } catch {
        return [];
    }
};

const formatInvalidMenuItem = (item: OrderIssueItem): string => {
    const quantity = item.Quantity || 1;
    const size = item.Size?.Name || item.Size?.ColloquialName;
    const itemName = [size, item.Name].filter(value => !!value).join(' ') || 'menu item';
    const reason = item.Reason?.trim();

    return reason
        ? `Invalid menu item: ${quantity} ${itemName} - ${reason}`
        : `Invalid menu item: ${quantity} ${itemName}`;
};

const isLastContentHit = (turnContentHit: any, sessionEvents: any[]): boolean => {
    const contentHits = (sessionEvents || []).filter(event => event?.eventType === 'ContentHit');
    if (!contentHits.length) {
        return false;
    }

    const lastContentHit = contentHits[contentHits.length - 1];
    if (turnContentHit === lastContentHit) {
        return true;
    }

    return !!turnContentHit?.requestId
        && turnContentHit.requestId === lastContentHit?.requestId
        && turnContentHit.eventDate === lastContentHit?.eventDate;
};

export const getOrderFailureReasonMarkdown = (turnEvents: any[], sessionEvents: any[]): string => {
    const hasTransfer = (turnEvents || []).some(event =>
        event?.eventType === 'CallTransferred'
    );
    if (!hasTransfer) {
        return '';
    }

    const turnContentHits = (turnEvents || []).filter(event => event?.eventType === 'ContentHit');
    const contentHit = turnContentHits[turnContentHits.length - 1];
    if (!contentHit || !isLastContentHit(contentHit, sessionEvents)) {
        return '';
    }

    const contentTitle = contentHit.response?.data?.title;
    if (typeof contentTitle === 'string' && contentTitle.trim().toLowerCase() === 'transfer me') {
        return '';
    }

    const originalRequest = contentHit.originalRequest;
    const sessionAttributes = originalRequest?.sessionAttributes || {};
    const flags = Array.isArray(originalRequest?.sessionFlags)
        ? originalRequest.sessionFlags
            .filter(flag => typeof flag === 'string')
            .map(flag => flag.toLowerCase())
        : [];
    const flagSet = new Set<string>(flags);
    const orderIssues = parseOrderIssues(sessionAttributes.orderIssues);
    const hasFailureFlag = FAILURE_FLAGS.some(flag => flagSet.has(flag));
    const orderWasAttempted = hasFailureFlag
        || !!sessionAttributes.order
        || !!orderIssues
        || ORDER_ATTEMPT_FLAGS.some(flag => flagSet.has(flag));

    if (!orderWasAttempted) {
        return '';
    }

    const reasons: string[] = [];
    if (flagSet.has('pos-api-failure')) {
        const posOrderErrors = parseStringArray(sessionAttributes['pos-order-errors']);

        if (posOrderErrors.length) {
            posOrderErrors.forEach(error => reasons.push(`POS order error: ${error}`));
        } else {
            reasons.push('The POS API failed while placing the order.');
        }
    }

    const invalidMenuItems = orderIssues?.InvalidMenuItems || orderIssues?.invalidMenuItems || [];
    if (invalidMenuItems.length) {
        invalidMenuItems.forEach(item => reasons.push(formatInvalidMenuItem(item)));
    } else if (flagSet.has('menu-item-not-found')) {
        reasons.push("The Order Extraction added a menu item ID that does not exist.");
    } else if (flagSet.has('invalid-menu-item') || flagSet.has('invalid-item-found')) {
        reasons.push('The order contains an invalid menu item.');
    }

    if (!flagSet.has('extract-flat-json-returned')) {
        reasons.push('Order extraction did not complete (extract-flat-json-returned is missing).');
    }

    if (!flagSet.has('update-order-complete')) {
        reasons.push('The order update did not complete (update-order-complete is missing).');
    }

    if (!reasons.length) {
        return '';
    }

    return `#### Why the order failed and was transferred\n${reasons.map(reason => `- ${reason}`).join('\n')}`;
};

export const getSessionOrderFailureReasonMarkdown = (sessionEvents: any[]): string => {
    const contentHits = (sessionEvents || []).filter(event => event?.eventType === 'ContentHit');
    const lastContentHit = contentHits[contentHits.length - 1];
    if (!lastContentHit?.requestId) {
        return '';
    }

    const lastSessionId = lastContentHit.sessionId || lastContentHit.sessionID;
    const turnEvents = sessionEvents.filter(event => {
        if (event?.requestId !== lastContentHit.requestId) {
            return false;
        }

        const eventSessionId = event.sessionId || event.sessionID;
        return !lastSessionId || !eventSessionId || eventSessionId === lastSessionId;
    });

    return getOrderFailureReasonMarkdown(turnEvents, sessionEvents);
};
